import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"}) : "";
const fmtDT   = d => d ? new Date(d).toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "";

const STATUS_DOT = { "Pick Up Request":"#7c3aed","Pick Up Accepted":"#4f46e5","Barcode Generated":"#0891b2","Picked Up":"#0284c7","In Transit":"#1d4ed8","Received":"#475569","Repairing":"#d97706","In Dispatch":"#ea580c","Out for Delivery":"#059669","Delivered":"#16a34a","In Repair":"#d97706","Completed":"#059669" };
const STATUS_EMOJI = { "Pick Up Request":"📞","Pick Up Accepted":"📅","Barcode Generated":"🏷️","Picked Up":"📦","In Transit":"🚚","Received":"🏪","Repairing":"🔧","In Dispatch":"📮","Out for Delivery":"🛵","Delivered":"✅","In Repair":"🔧","Completed":"✅" };

export default function PrintInvoice() {
  const { jobId } = useParams();
  const [job,      setJob]      = useState(null);
  const [sc,       setSC]       = useState(null);
  const [loading,  setLoading]  = useState(!!jobId);
  const [searching,setSearching]= useState(false);
  const [error,    setError]    = useState(null);
  const [search,   setSearch]   = useState("");

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSC(r.data)).catch(() => setSC(null));
    if (jobId) fetchJob(jobId);
  }, [jobId]);

  const fetchJob = async (id) => {
    setSearching(true); setError(null);
    try {
      const res = await axios.get(`${API}/jobs/${id}`);
      setJob(res.data);
    } catch { setError("Job not found. Please check the job number."); }
    finally  { setSearching(false); setLoading(false); }
  };

  const handleSearch = e => { e.preventDefault(); if (search.trim()) fetchJob(search.trim()); };

  const companyName = sc?.name || "XSTRONG POWER EV CHARGER";

  // Search screen (no job loaded)
  if (!jobId && !job) return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-3 shadow-sm">
        <img src="/logo.jpg" alt="XSTRONG POWER" className="h-10 w-auto object-contain" />
        <div>
          <p className="font-bold text-sm text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>{companyName}</p>
          <p className="text-xs text-green-600">{sc?.tagline || "Service Center"}</p>
        </div>
      </div>
      <div className="flex items-center justify-center min-h-[80vh] px-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 w-full max-w-md">
          <h2 className="text-xl font-semibold text-slate-900 mb-1" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Print Job Invoice</h2>
          <p className="text-sm text-slate-500 mb-6">Search by job number to preview and print</p>
          <form onSubmit={handleSearch}>
            <input
              type="text" placeholder="e.g. XP-2025-0001"
              value={search} onChange={e => setSearch(e.target.value.toUpperCase())}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-base font-mono focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              data-testid="print-search-input"
            />
            {error && <p className="text-sm text-rose-500 mt-2">{error}</p>}
            <button type="submit" disabled={searching || !search.trim()}
              className="w-full mt-4 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white py-3 rounded-xl font-semibold text-sm transition-colors">
              {searching ? "Searching..." : "Search & Preview"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  if (loading || searching) return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full" />
    </div>
  );

  if (error && !job) return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <p className="text-slate-500">{error}</p>
      <button onClick={() => { setJob(null); setError(null); }}
        className="text-sm text-green-600 hover:underline">Try another job number</button>
    </div>
  );

  return (
    <>
      {/* Print bar */}
      <div className="no-print fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2">
          <img src="/logo.jpg" alt="XSTRONG POWER" style={{ height: "36px", width: "auto", objectFit: "contain" }} />
          <span className="text-sm font-semibold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>{companyName}</span>
          <span className="text-xs text-slate-400 hidden sm:inline"> &#8212; Job {job.job_no}</span>
        </div>
        <div className="flex items-center gap-2">
          {!jobId && <button onClick={() => { setJob(null); setError(null); setSearch(""); }}
            className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5 border border-slate-200 rounded-lg">Search another</button>}
          <button onClick={() => window.print()}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-1.5 rounded-lg transition-colors"
            data-testid="invoice-print-button">
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2"/><rect x="7" y="17" width="10" height="7" rx="1"/><path d="M7 7V2h10v5"/></svg>
            Print
          </button>
        </div>
      </div>

      {/* Invoice */}
      <div className="mx-auto bg-white pt-20 pb-10 px-10 print:pt-0" style={{maxWidth:"794px",minHeight:"1123px"}} data-testid="invoice-print-root">
        {/* Header */}
        <div style={{borderBottom:"2px solid #16a34a",paddingBottom:"16px",marginBottom:"20px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div style={{display:"flex",alignItems:"center",gap:"14px"}}>
              <img src="/logo.jpg" alt="XSTRONG POWER" style={{height:"60px",width:"auto",objectFit:"contain"}} />
              <div>
                <h1 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:"22px",fontWeight:700,color:"#16a34a",margin:0}}>{companyName}</h1>
                <p style={{fontSize:"12px",fontWeight:600,color:"#15803d",margin:"2px 0 4px"}}>{sc?.tagline || "EV Charger Repair & Service Center"}</p>
                {sc?.phone && <p style={{fontSize:"11px",color:"#64748b",margin:"2px 0"}}>&#128222; {sc.phone}{sc.phone2 ? ` / ${sc.phone2}` : ""}</p>}
                {sc?.email && <p style={{fontSize:"11px",color:"#64748b",margin:"2px 0"}}>&#9993; {sc.email}</p>}
                {sc?.website && <p style={{fontSize:"11px",color:"#64748b",margin:"2px 0"}}>&#127760; {sc.website}</p>}
                {(sc?.address||sc?.city) && <p style={{fontSize:"11px",color:"#64748b",margin:"2px 0"}}>&#128205; {[sc?.address,sc?.city,sc?.state,sc?.pincode].filter(Boolean).join(", ")}</p>}
                {sc?.working_hours && <p style={{fontSize:"10px",color:"#94a3b8",margin:"2px 0"}}>&#128336; {sc.working_hours}</p>}
                {sc?.gst_number && <p style={{fontSize:"10px",color:"#94a3b8",fontFamily:"monospace",margin:"2px 0"}}>GST: {sc.gst_number}</p>}
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <p style={{fontSize:"20px",fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,color:"#1e293b",margin:0}}>{job.job_no}</p>
              <p style={{fontSize:"11px",color:"#64748b",margin:"4px 0 6px"}}>Date: {fmtDate(job.created_at)}</p>
              <div style={{display:"inline-block",padding:"3px 12px",borderRadius:"20px",fontSize:"11px",fontWeight:600,
                background:"#f0fdf4",color:"#15803d",border:"1px solid #bbf7d0"}}>
                {STATUS_EMOJI[job.current_status] || "🔄"} {job.current_status}
              </div>
            </div>
          </div>
        </div>

        {/* Barcode Section */}
        <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",padding:"14px",marginBottom:"20px",display:"flex",alignItems:"center",gap:"24px"}}>
          <div>
            <p style={{fontSize:"10px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em",margin:"0 0 8px"}}>Shipment Barcode</p>
            <Barcode value={job.job_no} width={1.5} height={45} fontSize={11} margin={4} background="#ffffff" />
          </div>
          <div style={{width:"1px",height:"60px",background:"#e2e8f0"}}/>
          <div>
            <p style={{fontSize:"10px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em",margin:"0 0 8px"}}>QR — Customer Track</p>
            <QRCodeSVG value={`${window.location.origin}/track?job=${job.job_no}`} size={56} level="M" includeMargin={false}/>
          </div>
          <div style={{flex:1}}>
            <p style={{fontSize:"11px",color:"#64748b",margin:0}}>Scan QR to track repair status online</p>
            <p style={{fontSize:"10px",color:"#94a3b8",marginTop:"4px"}}>Attach this barcode to the device/packaging</p>
          </div>
        </div>

        {/* Customer Details only — Charger table replaces product details */}
        <div style={{marginBottom:"20px"}}>
          <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",padding:"14px",maxWidth:"50%"}}>
            <p style={{fontSize:"10px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em",margin:"0 0 10px"}}>Customer Details</p>
            <Row label="Name"    value={job.customer?.name}/>
            <Row label="Phone"   value={[job.customer?.phone, job.customer?.alt_phone].filter(Boolean).join(" / ")}/>
            <Row label="Address" value={[job.customer?.address, job.customer?.landmark ? `Near: ${job.customer.landmark}` : null, job.customer?.city, job.customer?.state, job.customer?.pincode].filter(Boolean).join(", ")}/>
          </div>
        </div>

        {/* Charger Details */}
        <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",padding:"14px",marginBottom:"20px"}}>
          <p style={{fontSize:"10px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em",margin:"0 0 10px"}}>
            Charger Details ({(job.products||[]).length} unit{(job.products||[]).length!==1?"s":""})
          </p>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{borderBottom:"1px solid #e2e8f0"}}>
                {["#","Battery Cut-off / Ampere","Serial Number","Purchase Date","Body Type"].map(h=>(
                  <th key={h} style={{textAlign:"left",fontSize:"10px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",padding:"4px 8px",letterSpacing:"0.04em"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(job.products||[]).map((p,i)=>(
                <tr key={`prod-${p.serial_number || p.charger_spec || i}-${i}`} style={{borderBottom:i<(job.products||[]).length-1?"1px solid #f1f5f9":"none"}}>
                  <td style={{padding:"5px 8px",fontSize:"11px",color:"#94a3b8",fontWeight:600}}>{i+1}</td>
                  <td style={{padding:"5px 8px",fontSize:"12px",fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,color:"#16a34a"}}>{p.charger_spec||"—"}</td>
                  <td style={{padding:"5px 8px",fontSize:"11px",fontFamily:"monospace",color:"#1e293b"}}>{p.serial_number||"—"}</td>
                  <td style={{padding:"5px 8px",fontSize:"12px",color:"#1e293b"}}>{p.purchase_date||"—"}</td>
                  <td style={{padding:"5px 8px",fontSize:"11px",color:"#1e293b"}}>{p.body_type||"—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Fault & Notes */}
        <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",padding:"14px",marginBottom:"20px"}}>
          <p style={{fontSize:"10px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em",margin:"0 0 10px"}}>Fault &amp; Specifications</p>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"12px"}}>
            <NoteBlock title="Fault Description" value={job.specs?.fault_description}/>
            <NoteBlock title="Technical Notes"   value={job.specs?.technical_notes}/>
            <NoteBlock title="Repair Notes"      value={job.specs?.repair_notes}/>
          </div>
        </div>

        {/* Courier Details — only shown if added */}
        {job.courier_details?.awb_number && (
          <div style={{border:"2px solid #bfdbfe",borderRadius:"10px",padding:"14px",marginBottom:"20px",background:"#eff6ff"}}>
            <p style={{fontSize:"10px",fontWeight:700,color:"#3b82f6",textTransform:"uppercase",letterSpacing:"0.05em",margin:"0 0 10px"}}>🚚 Courier / Dispatch Details</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
              <div>
                <span style={{fontSize:"10px",color:"#64748b"}}>Courier Service: </span>
                <span style={{fontSize:"12px",fontWeight:600,color:"#1e293b"}}>{job.courier_details.courier_name||"—"}</span>
              </div>
              <div>
                <span style={{fontSize:"10px",color:"#64748b"}}>AWB / Tracking No.: </span>
                <span style={{fontSize:"12px",fontWeight:700,color:"#2563eb",fontFamily:"'IBM Plex Mono',monospace"}}>{job.courier_details.awb_number}</span>
              </div>
              {job.courier_details.tracking_url && (
                <div style={{gridColumn:"1/-1"}}>
                  <span style={{fontSize:"10px",color:"#64748b"}}>Track at: </span>
                  <span style={{fontSize:"11px",color:"#2563eb",fontFamily:"monospace"}}>{job.courier_details.tracking_url}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* History */}
        <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",padding:"14px",marginBottom:"20px"}}>
          <p style={{fontSize:"10px",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em",margin:"0 0 10px"}}>Service History</p>
          {job.status_history?.map((e,i) => (
            <div key={`${e.status}-${e.timestamp || i}`} style={{display:"flex",alignItems:"flex-start",gap:"10px",marginBottom:"6px"}}>
              <div style={{width:"8px",height:"8px",borderRadius:"50%",marginTop:"4px",flexShrink:0,background:STATUS_DOT[e.status]||"#94a3b8"}}/>
              <div>
                <span style={{fontSize:"12px",fontWeight:600,color:"#1e293b"}}>{e.status}</span>
                {e.notes&&<span style={{fontSize:"11px",color:"#64748b",marginLeft:"8px"}}>{e.notes}</span>}
                <span style={{fontSize:"11px",color:"#94a3b8",marginLeft:"8px"}}>{fmtDT(e.timestamp)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Signatures */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"30px",borderTop:"1px solid #e2e8f0",paddingTop:"20px"}}>
          <SignBox label="Customer Signature"/>
          <SignBox label={`Authorized Signature (${companyName})`}/>
        </div>
        <div style={{marginTop:"24px",textAlign:"center"}}>
          <p style={{fontSize:"10px",color:"#94a3b8"}}>{companyName} &#8212; Thank you for choosing us!</p>
        </div>
      </div>
    </>
  );
}

const Row = ({label,value,mono,bold}) => (
  <div style={{display:"flex",gap:"6px",marginBottom:"5px"}}>
    <span style={{fontSize:"11px",color:"#94a3b8",minWidth:"70px",flexShrink:0}}>{label}:</span>
    <span style={{fontSize:"12px",color:bold?"#15803d":"#1e293b",fontWeight:bold?700:400,fontFamily:mono?"'IBM Plex Mono',monospace":"inherit"}}>{value||"\u2014"}</span>
  </div>
);
const NoteBlock = ({title,value}) => (
  <div>
    <p style={{fontSize:"10px",fontWeight:600,color:"#64748b",margin:"0 0 4px"}}>{title}</p>
    <p style={{fontSize:"11px",color:value?"#1e293b":"#94a3b8",fontStyle:value?"normal":"italic",margin:0,lineHeight:1.5}}>{value||"Not specified"}</p>
  </div>
);
const SignBox = ({label}) => (
  <div>
    <div style={{height:"50px",borderBottom:"1px solid #cbd5e1"}}/>
    <p style={{fontSize:"10px",color:"#94a3b8",marginTop:"6px"}}>{label}</p>
  </div>
);
